import { memo, useCallback } from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@doger/ui/components/ui/dialog';
import { ConfirmEmailForm } from '@doger/core/modules/auth';
import { useJWT } from '@doger/core/modules/auth';
import { JwtDecoder } from '@doger/core/modules/auth/jwt';

import {
  LogAmplitudeEventOnMount,
  useAnalytics,
} from '@doger/chat/shared/analytics';

import { useTranslation } from '../../shared/i18n';
import { useAuth } from './auth.context';
import styles from './email-confirmation-modal.module.scss';

export const EmailConfirmationModal = memo(() => {
  const jwt = useJWT();
  const { t } = useTranslation('app');
  const {
    isEmailConfirmationModalOpen,
    setIsEmailConfirmationModalOpen,
    setIsWhitelistModalOpen,
  } = useAuth();
  const analytics = useAnalytics();

  const onClose = useCallback(() => {
    setIsEmailConfirmationModalOpen(false);
  }, [setIsEmailConfirmationModalOpen]);

  const onSuccess = useCallback(
    (tokens: { accessToken: string; refreshToken: string }) => {
      analytics.amplitude.track({ event: 'User Confirmed Email' });

      setIsEmailConfirmationModalOpen(false);

      const result = JwtDecoder.decodeAccessToken(tokens.accessToken);

      if (result.success) {
        if (!result.data.completedProfile) {
          setIsWhitelistModalOpen(true);
        }
      }
    },
    [setIsEmailConfirmationModalOpen, setIsWhitelistModalOpen]
  );

  if (!jwt) {
    return null;
  }

  return (
    <Dialog open={isEmailConfirmationModalOpen} onOpenChange={onClose}>
      <DialogContent className={styles.modal}>
        <LogAmplitudeEventOnMount event='Confirm Email Modal Viewed' />

        <DialogHeader className={styles.header} center>
          <DialogTitle>{t('emailConfirmation.title')}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <ConfirmEmailForm email={jwt.email} onSuccess={onSuccess} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
});
